import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface HomepageSections {
  hero: boolean;
  case_studies: boolean;
  philosophy: boolean;
  thinking: boolean;
  footer: boolean;
}

const DEFAULT_SECTIONS: HomepageSections = {
  hero: true,
  case_studies: true,
  philosophy: true,
  thinking: true,
  footer: true,
};

export function useSiteSettings<T>(key: string) {
  return useQuery({
    queryKey: ['site-settings', key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', key)
        .maybeSingle();

      if (error) throw error;
      return data?.value as T | null;
    },
  });
}

export function useHomepageSections() {
  const { data, isLoading, error } = useSiteSettings<HomepageSections>('homepage_sections');
  
  return {
    sections: data ?? DEFAULT_SECTIONS,
    isLoading,
    error,
  };
}

export function useUpdateSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: HomepageSections }) => {
      const { error } = await supabase
        .from('site_settings')
        .update({ value: value as unknown as Record<string, boolean> })
        .eq('key', key);

      if (error) throw error;
    },
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', key] });
    },
  });
}
